import { Brand } from '../../../shared/components/Brand'
import { exampleSolution } from '../game24/solver'
import { prettyExpr } from '../game24/format'

// Minimal shape so this accepts either a PuzzlePlay or a PuzzleTrace.
type SummaryItem = { numbers: number[]; correct: boolean; answerMode: string | null }

// End-of-test summary: each puzzle with how it was solved (spoken / typed) or not, plus
// the overall score, and a call-to-action (e.g. unlock free play).
const statusFor = (p: SummaryItem): { label: string; cls: string } => {
  if (p.correct && p.answerMode === 'voice-detected') return { label: '✅ Solved (spoken)', cls: 'text-[#398874]' }
  if (p.correct) return { label: '✅ Solved (typed)', cls: 'text-[#398874]' }
  if (p.answerMode === 'timeout') return { label: '⏱ Ran out of time', cls: 'text-gray-400' }
  return { label: '✕ Not solved', cls: 'text-gray-400' }
}

export const SummaryScreen = ({
  plays,
  saveError,
  action,
  feedback,
}: {
  plays: SummaryItem[]
  saveError: string | null
  action?: { label: string; onClick: () => void }
  // When present, a skippable feedback prompt is shown instead of the free-play
  // action (both paths lead onward). Omitted once the survey is already done.
  feedback?: { onGive: () => void; onSkip: () => void }
}) => {
  const solved = plays.filter((p) => p.correct).length

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-8">
      <Brand game="twentyfour" />
      <div className="mt-8 w-full max-w-md text-center">
        <div className="text-5xl mb-2">🎯</div>
        <h2 className="text-xl font-semibold text-gray-800">
          You solved {solved} of {plays.length}
        </h2>

        {feedback && (
          <div className="mt-6 rounded-xl border border-[#820458]/20 bg-[#820458]/5 p-4">
            <p className="text-sm text-gray-600">
              Before you go — a couple of minutes of feedback would really help our research.
            </p>
            <button
              type="button"
              onClick={feedback.onGive}
              className="mt-3 w-full rounded-lg bg-[#820458] py-3 font-semibold text-white"
            >
              Give feedback (2 min)
            </button>
            <button type="button" onClick={feedback.onSkip} className="mt-2 w-full text-sm text-gray-400 underline">
              No thanks
            </button>
          </div>
        )}

        <ul className="mt-5 divide-y divide-gray-100 rounded-lg border border-gray-100">
          {plays.map((p, i) => {
            const status = statusFor(p)
            const sol = exampleSolution(p.numbers)
            return (
              <li key={i} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-gray-700">{p.numbers.join('  ')}</span>
                  <span className={`text-sm font-medium ${status.cls}`}>{status.label}</span>
                </div>
                {sol && (
                  <div className="mt-1 text-left font-mono text-xs text-gray-400">{prettyExpr(sol)} = 24</div>
                )}
              </li>
            )
          })}
        </ul>

        <p className="mt-6 text-sm text-gray-500">
          Thanks for thinking out loud — every session helps us learn how people reason through puzzles.
        </p>

        {!feedback && action && (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-6 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold"
          >
            {action.label}
          </button>
        )}

        {saveError && (
          <p className="mt-3 text-xs text-gray-400">
            (We hit a snag saving some of your results, but your participation still counts.)
          </p>
        )}
      </div>
    </div>
  )
}
